import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { UserContext } from "../context/UserContext";
import Swal from "sweetalert2";
import "./FormularioArriendo.css";

const meses = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
];

const FormularioArriendo = ({ cliente, año, onClose, onSave }) => {
  const { currentUser } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [factores, setFactores] = useState({});
  const [arriendosAnteriores, setArriendosAnteriores] = useState([]);
  const [arriendo, setArriendo] = useState({
    año,
    clienteId: cliente._id,
    empresa: cliente.nombre || "",
    rutEmpresa: cliente.rut || "",
    rutRepresentante: "",
    rutArrendador: "",
    nombreArrendador: "",
    ciudad: "",
    roles: [{ numero: "", valorTotal: "" }],
    valoresMensuales: meses.map(mes => ({
      mes,
      valor: "",
      valorActualizado: ""
    })),
    estado: "pendiente"
  });

  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${currentUser?.token}`
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [factRes, arrRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/factores-arriendo/year/${año}`, getAuthHeader()),
          axios.get(`${process.env.REACT_APP_API_URL}/arriendos/cliente/${cliente._id}`, getAuthHeader())
        ]);

        const factoresMap = {};
        factRes.data.forEach(f => {
          factoresMap[f.mes] = f.factor;
        });
        setFactores(factoresMap);
        setArriendosAnteriores(arrRes.data);
      } catch (error) {
        console.error("Error cargando datos:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Error cargando datos necesarios"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [cliente._id, año]);

  const handleInputChange = (field, value) => {
    setArriendo(prev => ({ ...prev, [field]: value }));
  };

  const handleRolChange = (index, field, value) => {
    const nuevosRoles = [...arriendo.roles];
    nuevosRoles[index] = { ...nuevosRoles[index], [field]: value };
    setArriendo(prev => ({ ...prev, roles: nuevosRoles }));
  };

  const agregarRol = () => {
    setArriendo(prev => ({
      ...prev,
      roles: [...prev.roles, { numero: "", valorTotal: "" }]
    }));
  };

  const eliminarRol = (index) => {
    if (arriendo.roles.length === 1) {
      Swal.fire({
        icon: "warning",
        title: "Atención",
        text: "Debe existir al menos un rol"
      });
      return;
    }
    const nuevosRoles = arriendo.roles.filter((_, i) => i !== index);
    setArriendo(prev => ({ ...prev, roles: nuevosRoles }));
  };

  const handleValorMensualChange = (mes, valor) => {
    const nuevosValores = arriendo.valoresMensuales.map(vm => {
      if (vm.mes === mes) {
        const factor = factores[mes] || 1;
        const valorActualizado = valor !== "" && !isNaN(parseFloat(valor))
          ? (parseFloat(valor) * factor).toFixed(0)
          : "";
        return { ...vm, valor, valorActualizado };
      }
      return vm;
    });
    setArriendo(prev => ({ ...prev, valoresMensuales: nuevosValores }));
  };

  const calcularTotales = () => {
    const sumaTotal = arriendo.valoresMensuales.reduce(
      (sum, vm) => sum + (parseFloat(vm.valor) || 0),
      0
    );
    const numRoles = arriendo.roles.length || 1;
    return {
      total: sumaTotal.toFixed(0),
      porRol: (sumaTotal / numRoles).toFixed(0)
    };
  };

  const copiarArriendo = (arrAnterior) => {
    const nuevoArriendo = {
      ...arrAnterior,
      _id: undefined,
      año,
      estado: "pendiente"
    };
    setArriendo(nuevoArriendo);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!arriendo.rutRepresentante || !arriendo.rutArrendador || !arriendo.ciudad) {
      Swal.fire({
        icon: "error",
        title: "Datos incompletos",
        text: "Por favor complete todos los campos obligatorios"
      });
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/arriendos`,
        arriendo,
        getAuthHeader()
      );

      Swal.fire({
        icon: "success",
        title: "Éxito",
        text: "Arriendo guardado correctamente"
      });

      onSave && onSave(response.data);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al guardar el arriendo"
      });
    }
  };

  if (loading) {
    return <div className="loading">Cargando datos...</div>;
  }

  const { total, porRol } = calcularTotales();

  return (
    <div className="formulario-arriendo">
      <div className="form-header">
        <h2>Arriendo - {año}</h2>
        <button className="close-button" onClick={onClose}>&times;</button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-group">
            <label>Empresa:</label>
            <input type="text" value={arriendo.empresa} disabled />
          </div>
          <div className="form-group">
            <label>RUT Empresa:</label>
            <input type="text" value={arriendo.rutEmpresa} disabled />
          </div>
          <div className="form-group">
            <label>RUT Representante:</label>
            <input
              type="text"
              value={arriendo.rutRepresentante}
              onChange={e => handleInputChange("rutRepresentante", e.target.value)}
              placeholder="Ej: 12345678-9"
            />
          </div>
          <div className="form-group">
            <label>RUT Arrendador:</label>
            <input
              type="text"
              value={arriendo.rutArrendador}
              onChange={e => handleInputChange("rutArrendador", e.target.value)}
              placeholder="Ej: 98765432-1"
            />
          </div>
          <div className="form-group">
            <label>Nombre Arrendador:</label>
            <input
              type="text"
              value={arriendo.nombreArrendador}
              onChange={e => handleInputChange("nombreArrendador", e.target.value)}
              placeholder="Ej: INMOBILIARIA LOS NOTROS, SPA"
            />
          </div>
          <div className="form-group">
            <label>Ciudad:</label>
            <input
              type="text"
              value={arriendo.ciudad}
              onChange={e => handleInputChange("ciudad", e.target.value)}
              placeholder="Ej: SAN FERNANDO"
            />
          </div>
        </div>

        <div className="roles-section">
          <h3>Roles</h3>
          <div className="roles-container">
            {arriendo.roles.map((rol, index) => (
              <div key={index} className="rol-item">
                <input
                  type="text"
                  value={rol.numero}
                  onChange={e => handleRolChange(index, "numero", e.target.value)}
                  placeholder="Ej: 80-005"
                />
                <button 
                  type="button" 
                  onClick={() => eliminarRol(index)}
                  className="delete-rol"
                >
                  ✕
                </button>
              </div>
            ))}
            <button type="button" onClick={agregarRol} className="add-rol">
              + Agregar Rol
            </button>
          </div>
        </div>

        <div className="valores-section">
          <h3>Valores Mensuales</h3>
          <div className="tabla-valores">
            <table>
              <thead>
                <tr>
                  <th>Mes</th>
                  <th>Valor</th>
                  <th>Factor</th>
                  <th>Valor Actualizado</th>
                </tr>
              </thead>
              <tbody>
                {arriendo.valoresMensuales.map(vm => (
                  <tr key={vm.mes}>
                    <td>{vm.mes}</td>
                    <td>
                      <input
                        type="number"
                        value={vm.valor}
                        onChange={e => handleValorMensualChange(vm.mes, e.target.value)}
                      />
                    </td>
                    <td>{factores[vm.mes]}</td>
                    <td>{vm.valorActualizado}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="totales">
            <p>Total Anual: ${Number(total).toLocaleString('es-CL')}</p>
            <p>Valor por Rol: ${Number(porRol).toLocaleString('es-CL')}</p>
          </div>
        </div>

        {arriendosAnteriores.length > 0 && (
          <div className="arriendos-anteriores">
            <h3>Arriendos Anteriores</h3>
            <div className="lista-arriendos">
              {arriendosAnteriores.map(arr => (
                <div key={arr._id} className="arriendo-anterior">
                  <span>Año {arr.año}</span>
                  <button type="button" onClick={() => copiarArriendo(arr)}>
                    Copiar
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="submit-button">
            Guardar Arriendo
          </button>
          <button type="button" onClick={onClose} className="cancel-button">
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormularioArriendo;